import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Plus } from 'lucide-react';
import { Skeleton } from '@shared/ui/feedback/Skeleton';
import { formatRelative } from '@shared/lib/format';
import { MarqueeText } from '@shared/ui/primitives/MarqueeText';
import { fetchSupportRequests, type SupportRequest } from '@features/support/api';
import { WidgetCard } from './WidgetCard';
import { widgetTitle, type WidgetProps } from './registry';

const WIDGET_REQUEST_LIMIT = 3;

/** Pill per open status: what needs the member stands out, plain "Open" stays quiet. */
const STATUS_PILL: Partial<Record<SupportRequest['status'], { label: string; bg: string; ink: string }>> = {
  awaiting_member: {
    label: 'Needs your reply',
    bg: 'color-mix(in oklab, var(--warning) 18%, var(--surface-card))',
    ink: 'color-mix(in oklab, var(--warning) 60%, var(--ink-primary))',
  },
  in_progress: {
    label: 'In progress',
    bg: 'color-mix(in oklab, var(--info) 14%, var(--surface-card))',
    ink: 'color-mix(in oklab, var(--info) 80%, var(--ink-primary))',
  },
  open: { label: 'Open', bg: 'var(--surface-sunken)', ink: 'var(--ink-secondary)' },
};

/**
 * Home widget: the member's open helpdesk requests (up to three, the ones
 * waiting on them first) with a status pill each; a row opens that request,
 * the rest of the card opens the full list, "New" jumps straight to logging one.
 */
export function SupportWidget({ config }: WidgetProps): JSX.Element {
  const navigate = useNavigate();
  const query = useQuery({ queryKey: ['support-requests', 'self'], queryFn: () => fetchSupportRequests() });

  const requests = openRequests(query.data ?? []).slice(0, WIDGET_REQUEST_LIMIT);

  return (
    <WidgetCard
      title={widgetTitle(config)}
      actionLabel="New"
      actionIcon={Plus}
      onAction={() => navigate('/app/support/requests/new')}
      onClick={() => navigate('/app/support/requests')}
      className={requests.length > 0 ? 'gap-0 p-0' : 'px-4'}
    >
      {query.isLoading ? (
        <Skeleton className="h-12 rounded-lg" />
      ) : requests.length === 0 ? (
        <p className="text-body-sm text-ink-muted">Nothing open right now.</p>
      ) : (
        <ul className="divide-y divide-hairline">
          {requests.map((request) => {
            const pill = STATUS_PILL[request.status]!;
            return (
              <li key={request.request_id}>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`/app/support/requests/${request.request_id}`);
                  }}
                  className="flex w-full items-center gap-3 px-4 py-[0.8125rem] text-left active:opacity-80"
                >
                  <span className="min-w-0 flex-1">
                    <MarqueeText className="text-[0.9375rem] font-semibold text-ink">{request.subject}</MarqueeText>
                    <MarqueeText className="text-[0.8125rem] text-ink-secondary">
                      {[request.reference, formatRelative(request.updated_at)].filter(Boolean).join(' · ')}
                    </MarqueeText>
                  </span>

                  <span
                    className="shrink-0 rounded-pill px-2.5 py-0.5 text-[0.8125rem] font-medium"
                    style={{ backgroundColor: pill.bg, color: pill.ink }}
                  >
                    {pill.label}
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </WidgetCard>
  );
}

/** Requests still open, the ones waiting on the member first, then most recently updated. */
export function openRequests<T extends Pick<SupportRequest, 'status' | 'updated_at'>>(requests: T[]): T[] {
  return requests
    .filter((r) => STATUS_PILL[r.status])
    .sort(
      (a, b) =>
        Number(b.status === 'awaiting_member') - Number(a.status === 'awaiting_member') ||
        new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime(),
    );
}
